import { useState, useCallback, useRef } from "react";

export type SortOrder = 'asc' | 'desc' | null;

export interface PaginatedResponse<T> {
  total: number;
  per_page: number;
  current_page: number;
  last_page: number;
  from: number;
  to: number;
  items: T[];
}

export type PaginatedFetcher<T> = (
  page: number,
  searchTerm: string,
  statusFilter: string | null,
  sortBy: string | null,
  sortOrder: SortOrder
) => Promise<PaginatedResponse<T>>;

export const usePaginatedKits = <T,>(fetcher: PaginatedFetcher<T>) => {
  const [kits, setKits] = useState<T[]>([]);
  const [totalResults, setTotalResults] = useState(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedFilter, setSelectedFilter] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<SortOrder>(null);
  
  // Store fetcher in ref to avoid recreating fetchPage when fetcher changes
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  const fetchPage = useCallback(async (
    page: number = 1,
    searchTerm?: string,
    statusFilter?: string | null,
    currentSortBy?: string | null,
    currentSortOrder?: SortOrder
  ) => {
    try {
      setLoading(true);
      // Use provided values or fall back to current state
      const finalSearchTerm = searchTerm ?? searchQuery;
      const finalStatusFilter = statusFilter === undefined ? selectedFilter : statusFilter;
      const finalSortBy = currentSortBy === undefined ? sortBy : currentSortBy;
      const finalSortOrder = currentSortOrder === undefined ? sortOrder : currentSortOrder;

      const data = await fetcherRef.current(
        page, 
        finalSearchTerm, 
        finalStatusFilter,
        finalSortBy,
        finalSortOrder
      );
      setKits(data.items);
      setTotalResults(data.total);
      setTotalPages(data.last_page);
      setCurrentPage(data.current_page);
      setError(null);
    } catch (err: any) {
      console.error("Error fetching kits:", err);
      setError(err?.message || "Failed to load patient test data");
    } finally {
      setLoading(false);
    }
  }, [searchQuery, selectedFilter, sortBy, sortOrder]);

  const handleSearch = () => {
    fetchPage(1, searchQuery, selectedFilter, sortBy, sortOrder);
  };

  const handlePageChange = (page: number) => {
    fetchPage(page, searchQuery, selectedFilter, sortBy, sortOrder);
  };

  const handleFilterChange = (filterValue: string | null) => {
    setSelectedFilter(filterValue);
    fetchPage(1, searchQuery, filterValue, sortBy, sortOrder);
  };

  const handleSort = (key: string) => {
    let nextSortBy: string | null = key;
    let nextSortOrder: SortOrder = 'desc';

    if (sortBy === key) {
      if (sortOrder === 'desc') {
        nextSortOrder = 'asc';
      } else if (sortOrder === 'asc') {
        nextSortBy = null;
        nextSortOrder = null;
      }
    }

    setSortBy(nextSortBy);
    setSortOrder(nextSortOrder);
    fetchPage(1, searchQuery, selectedFilter, nextSortBy, nextSortOrder);
  };

  return {
    kits,
    totalResults,
    loading,
    error,
    setError,
    currentPage,
    totalPages,
    selectedFilter,
    setSelectedFilter,
    searchQuery,
    setSearchQuery,
    sortBy,
    sortOrder,
    fetchPage,
    handleSearch,
    handlePageChange,
    handleFilterChange,
    handleSort,
  };
};

